import React, { useEffect, useRef, useState } from "react";
import { Loader2, Send, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { errMessage } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TicketAttachment } from "./TicketAttachment";
import { TicketStatusBadge } from "./TicketStatusBadge";

const FLAG_TEXT = {
  sender_mismatch: "Sent from a different address than the ticket owner — status not changed",
  auto_reply: "Automatic reply — status not changed",
  attachment_skipped: "An attachment was skipped (only images/PDF up to 5 MB are kept)",
};
const newClientId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const Message = ({ ticketId, m, onChange }) => {
  const admin = m.direction === "admin";
  return (
    <div className={`flex ${admin ? "justify-end" : "justify-start"}`} data-testid={`ticket-message-${m.id}`}>
      <div className={`max-w-[88%] rounded-lg border px-3.5 py-2.5 ${admin ? "border-[#0D9488]/30 bg-[#0D9488]/5" : "border-slate-200 bg-slate-50"}`}>
        <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-700">{admin ? `${m.author_name} (Support)` : m.author_name || m.author_email}</span>
          <span>{new Date(m.created_at).toLocaleString()}</span>
        </div>
        <p className="whitespace-pre-wrap break-words text-sm text-slate-700">{m.body}</p>
        {(m.flags || []).map((f) => (
          <p key={f} className="mt-1.5 flex items-center gap-1 text-xs text-amber-700" data-testid={`ticket-flag-${f}`}><AlertTriangle className="h-3 w-3" /> {FLAG_TEXT[f] || f}</p>
        ))}
        {m.attachments.map((a) => <TicketAttachment key={a.id} ticketId={ticketId} att={a} onChange={onChange} />)}
      </div>
    </div>
  );
};

export const TicketSheet = ({ ticketId, onClose, onUpdated }) => {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const clientId = useRef(newClientId());

  useEffect(() => {
    if (!ticketId) { setT(null); return; }
    setReply(""); clientId.current = newClientId();
    api.get(`/admin/support/tickets/${ticketId}`).then((r) => setT(r.data)).catch((e) => toast.error(errMessage(e)));
  }, [ticketId]);

  const update = (data) => { setT(data); onUpdated(); };
  const setStatus = async (status) => {
    try { update((await api.patch(`/admin/support/tickets/${ticketId}/status`, { status })).data); toast.success(`Marked ${status}.`); }
    catch (e) { toast.error(errMessage(e)); }
  };
  const send = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      update((await api.post(`/admin/support/tickets/${ticketId}/reply`, { body: reply, client_id: clientId.current })).data);
      setReply(""); clientId.current = newClientId(); toast.success("Reply sent.");
    } catch (e) { toast.error(errMessage(e)); } finally { setSending(false); }
  };

  return (
    <Sheet open={Boolean(ticketId)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl" data-testid="ticket-sheet">
        {!t ? <div className="flex flex-1 items-center justify-center"><SheetTitle className="sr-only">Loading ticket</SheetTitle><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <>
            <SheetHeader className="space-y-2 border-b border-slate-200 p-5 text-left">
              <div className="flex items-center gap-2 text-xs text-slate-500"><span className="font-mono" data-testid="ticket-ref">{t.ticket_ref}</span>{t.category && <span>· {t.category}</span>}</div>
              <SheetTitle className="pr-6 text-lg" data-testid="ticket-subject">{t.subject}</SheetTitle>
              <SheetDescription className="text-xs">{t.name} · {t.email}</SheetDescription>
              {/^https?:\/\//i.test(t.project_link || "") && <a href={t.project_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0D9488] hover:underline" data-testid="ticket-project-link"><ExternalLink className="h-3 w-3" />{t.project_link}</a>}
              <div className="flex items-center gap-3 pt-1">
                <TicketStatusBadge status={t.status} />
                <Select value={t.status} onValueChange={setStatus}>
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["open", "pending", "resolved"].map((s) => <SelectItem key={s} value={s} data-testid={`ticket-status-${s}`} className="capitalize">{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {t.retention_deletes_at && <p className="text-xs text-slate-500" data-testid="ticket-retention-note">Attachments will be deleted on {new Date(t.retention_deletes_at).toLocaleDateString()} (90 days after resolution).</p>}
            </SheetHeader>
            <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="ticket-thread">
              {t.messages.map((m) => <Message key={m.id} ticketId={t.id} m={m} onChange={update} />)}
            </div>
            <div className="space-y-2 border-t border-slate-200 p-4">
              <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Reply to ${t.email} from support@getciteqr.com…`} data-testid="ticket-reply-input" />
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-slate-500">Sending sets the ticket to Pending.</p>
                <Button onClick={send} disabled={!reply.trim() || sending} className="bg-[#0D9488] hover:bg-[#0B7F75]" data-testid="ticket-reply-send">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Send reply
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
